/**
 * Implements a panel with buttons for manipulation
 */
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { useAccount } from 'wagmi';
import { ILimitOrder } from '@kattana-io/common';
import { enums } from '@kattana-io/common';
import { actionDeleteLimitOrder } from '../../../actions/limit_orders';
import { actionShowModal } from '../../../actions/modal';
import { actionCreateNotification } from '../../../actions/notifications';
import { IStore } from '../../../reducers';
import { ModalTypes } from '../../../reducers/modal';
import { authTokenSelector } from '../../../selectors/auth';
import { decorateLimitOrderStatus } from '../../../helpers/decorators';

interface props {
  order: ILimitOrder;
  filterEnabled: boolean;
  onFilter: (input: string) => void;
}

/**
 * Cache delete action
 */
const useDeleteAction = (order: ILimitOrder, dispatch: any) => {
  const { address } = useAccount();
  const token = useSelector(authTokenSelector);

  const onDelete = useCallback(async () => {
    if (order.status != enums.LimitOrderStatus.WATCH) {
      dispatch(
        actionCreateNotification({
          kind: 'warning',
          message: `You can't delete order with status ${decorateLimitOrderStatus(
            order.status
          )}`,
        })
      );
      return;
    }

    dispatch(
      actionDeleteLimitOrder({
        data: {
          uuid: order.uuid,
          wallet: address || '',
        },
        token,
      })
    );
  }, [order]);

  return {
    onDelete,
  };
};

const useEditFlags = (order: ILimitOrder) => {
  const canBeEdited = order.status === 'WATCH' && order.execution_type != 'GRID';
  const mVisible = useSelector(
    (state: IStore) => state.modal.modalName === ModalTypes.limitOrderEdit
  );
  const mId = useSelector((state: IStore) => state.modal.props.order?.uuid);
  const isBeingEdited = mVisible ? mId === order.uuid : false;

  return {
    canBeEdited,
    isBeingEdited,
  };
};

const Actions = ({ order, filterEnabled, onFilter }: props) => {
  const dispatch = useDispatch();
  const { onDelete } = useDeleteAction(order, dispatch);
  const { isBeingEdited, canBeEdited } = useEditFlags(order);
  const onEditClicked = () =>
    dispatch(actionShowModal(ModalTypes.limitOrderEdit, { order }));

  const onFilterClicked = () => {
    if (filterEnabled) {
      onFilter('');
    } else {
      onFilter(`${order.chain}/${order.pair}`);
    }
  };

  return (
    <div className='actions'>
      {canBeEdited && (
        <div
          className={`action ${isBeingEdited ? 'action--active' : ''}`}
          onClick={onEditClicked}
          data-for={`limit-orders-actions-tooltip-${order.uuid}`}
          data-tip='Edit price'
        >
          <div className='action__icon icon-edit-square' />
        </div>
      )}
      <div
        className={`action ${filterEnabled ? 'action--active' : ''}`}
        onClick={onFilterClicked}
        data-for={`limit-orders-actions-tooltip-${order.uuid}`}
        data-tip='Filter only this pair'
      >
        <div className='action__icon icon-filter' />
      </div>
      <div
        className='action'
        onClick={onDelete}
        data-for={`limit-orders-actions-tooltip-${order.uuid}`}
        data-tip='Cancel'
      >
        <div className='action__icon icon-trash' />
      </div>
      <ReactTooltip
        id={`limit-orders-actions-tooltip-${order.uuid}`}
        place='left'
        effect='solid'
        overridePosition={({ left }) => {
          return {
            left: left,
            top: -2,
          };
        }}
        className='actions__tooltip text_tooltip'
      />
    </div>
  );
};

export default Actions;
